import React, { useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { IoSettingsOutline } from 'react-icons/io5';

import { PRIVACY_FOOTER_COPYRIGHT } from 'constants/privacy-banner';
import { usePrivacyConfig } from '../hooks';
import { openConsentBanner } from './PrivacyBanner.helpers';

// Floating buttons (e.g. F1 Assistant) read this to stay above the bar
const FOOTER_HEIGHT_VARIABLE = '--privacy-footer-height';

// Callback ref: keeps the CSS variable equal to the bar's height while it is shown (it wraps on
// narrow screens) and removes it when the bar is hidden
function useFooterHeightVariable() {
    const observerRef = useRef<ResizeObserver>();

    return useCallback((node: HTMLElement | null) => {
        const root = document.documentElement;
        observerRef.current?.disconnect();

        if (!node) {
            root.style.removeProperty(FOOTER_HEIGHT_VARIABLE);
            return;
        }

        const update = () => root.style.setProperty(FOOTER_HEIGHT_VARIABLE, `${node.offsetHeight}px`);
        update();
        observerRef.current = new ResizeObserver(update);
        observerRef.current.observe(node);
    }, []);
}

function PrivacyFooter() {
    const config = usePrivacyConfig();
    const footerRef = useFooterHeightVariable();

    if (!config) {
        return null;
    }

    const links = [
        { label: 'Privacy Notice', href: config.privacy_notice_en?.trim() },
        { label: 'Cookie Notice', href: config.cookie_notice_en?.trim() },
        { label: 'นโยบายคุ้มครองข้อมูลส่วนบุคคล', href: config.privacy_notice_th?.trim() },
        { label: 'นโยบายการใช้คุกกี้', href: config.cookie_notice_th?.trim() }
    ].filter((link): link is { label: string; href: string } => !!link.href);

    // Replaces reddot's floating gear (hidden by PrivacyBanner)
    const hasConsentBanner = !!config.script_banner?.trim();

    if (links.length === 0 && !hasConsentBanner) {
        return null;
    }

    return (
        <footer
            ref={footerRef}
            className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-white px-4 py-2 text-gray-600"
        >
            <span className="text-xs">{PRIVACY_FOOTER_COPYRIGHT}</span>
            <nav className="flex flex-wrap items-center gap-x-2 text-sm">
                {links.map((link, index) => (
                    <React.Fragment key={link.label}>
                        {index > 0 && <span aria-hidden="true">|</span>}
                        <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900 hover:underline"
                        >
                            {link.label}
                        </a>
                    </React.Fragment>
                ))}
                {hasConsentBanner && (
                    <button
                        type="button"
                        title="Cookie Settings"
                        aria-label="Cookie Settings"
                        onClick={openConsentBanner}
                        className="ml-2 inline-flex items-center rounded p-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <IoSettingsOutline size={18} />
                    </button>
                )}
            </nav>
        </footer>
    );
}

export default observer(PrivacyFooter);
